import { useQuery, useMutation } from '@tanstack/react-query'
import { salesReportsApi } from '../lib/apiSalesReports'
import type {
  OverviewResponse,
  PipelineResponse,
  RepsResponse,
  SourcesResponse,
  TrendsResponse,
  CanvasingResponse,
  LeaderboardResponse,
  SalesReportParams,
  TrendsParams,
  ExportParams,
  LeaderboardParams,
} from '../types/salesReports'

/**
 * Hook for fetching sales overview metrics with period comparisons
 */
export const useSalesOverview = (params?: SalesReportParams, enabled = true) => {
  return useQuery<OverviewResponse>({
    queryKey: ['sales-reports', 'overview', params],
    queryFn: () => salesReportsApi.overview(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for fetching pipeline analytics
 */
export const useSalesPipeline = (params?: SalesReportParams, enabled = true) => {
  return useQuery<PipelineResponse>({
    queryKey: ['sales-reports', 'pipeline', params],
    queryFn: () => salesReportsApi.pipeline(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for fetching sales rep performance metrics
 */
export const useSalesReps = (params?: SalesReportParams, enabled = true) => {
  return useQuery<RepsResponse>({
    queryKey: ['sales-reports', 'reps', params],
    queryFn: () => salesReportsApi.reps(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for fetching lead source performance
 */
export const useSalesSources = (params?: SalesReportParams, enabled = true) => {
  return useQuery<SourcesResponse>({
    queryKey: ['sales-reports', 'sources', params],
    queryFn: () => salesReportsApi.sources(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for fetching historical trends
 */
export const useSalesTrends = (params?: TrendsParams, enabled = true) => {
  return useQuery<TrendsResponse>({
    queryKey: ['sales-reports', 'trends', params],
    queryFn: () => salesReportsApi.trends(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for fetching canvasing metrics
 */
export const useSalesCanvasing = (params?: SalesReportParams, enabled = true) => {
  return useQuery<CanvasingResponse>({
    queryKey: ['sales-reports', 'canvasing', params],
    queryFn: () => salesReportsApi.canvasing(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for fetching team leaderboard with composite scores
 */
export const useSalesLeaderboard = (params?: LeaderboardParams, enabled = true) => {
  return useQuery<LeaderboardResponse>({
    queryKey: ['sales-reports', 'leaderboard', params],
    queryFn: () => salesReportsApi.leaderboard(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for exporting report data as CSV
 */
export const useExportSalesReport = () => {
  return useMutation({
    mutationFn: (params: ExportParams) => salesReportsApi.downloadCsv(params),
  })
}

/**
 * Utility to get date range presets
 */
export const getDateRangePresets = () => {
  const today = new Date()
  const formatDate = (date: Date) => date.toISOString().split('T')[0]

  // This month
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)

  // Last month
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)

  // This quarter
  const currentQuarter = Math.floor(today.getMonth() / 3)
  const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1)

  // Last quarter
  const lastQuarterStart = new Date(today.getFullYear(), (currentQuarter - 1) * 3, 1)
  const lastQuarterEnd = new Date(today.getFullYear(), currentQuarter * 3, 0)

  // This year
  const thisYearStart = new Date(today.getFullYear(), 0, 1)

  // Last year
  const lastYearStart = new Date(today.getFullYear() - 1, 0, 1)
  const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31)

  // Last 30 days
  const last30DaysStart = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

  // Last 90 days
  const last90DaysStart = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)

  return {
    thisMonth: {
      label: 'This Month',
      from: formatDate(thisMonthStart),
      to: formatDate(today),
    },
    lastMonth: {
      label: 'Last Month',
      from: formatDate(lastMonthStart),
      to: formatDate(lastMonthEnd),
    },
    thisQuarter: {
      label: 'This Quarter',
      from: formatDate(thisQuarterStart),
      to: formatDate(today),
    },
    lastQuarter: {
      label: 'Last Quarter',
      from: formatDate(lastQuarterStart),
      to: formatDate(lastQuarterEnd),
    },
    thisYear: {
      label: 'This Year',
      from: formatDate(thisYearStart),
      to: formatDate(today),
    },
    lastYear: {
      label: 'Last Year',
      from: formatDate(lastYearStart),
      to: formatDate(lastYearEnd),
    },
    last30Days: {
      label: 'Last 30 Days',
      from: formatDate(last30DaysStart),
      to: formatDate(today),
    },
    last90Days: {
      label: 'Last 90 Days',
      from: formatDate(last90DaysStart),
      to: formatDate(today),
    },
  }
}

export type DateRangePresetKey = keyof ReturnType<typeof getDateRangePresets>
